import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '@app/account/services/account.service';
import { OnboardingRequiredGuard } from '@app/learner-home/guards/onboarding-required.guard';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { DfFormFieldBuilder, DfFormFieldConfig } from '@degreed/fresco';
import { FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

/**
 * Change Password component is the form to change your password from the account screen.
 */
@Component({
  selector: 'dgx-change-password-form',
  templateUrl: './change-password-form.component.html',
})
export class ChangePasswordFormComponent
  extends SubscriberBaseDirective
  implements AfterViewInit
{
  public validationSummary: string;
  public model: { [key: string]: string | number } = {};
  public options: FormlyFormOptions = {};
  public form: FormGroup = new FormGroup({});
  public fields: DfFormFieldConfig[] = [];
  public isSaving: boolean = false;
  public i18n = this.translate.instant([
    'Core_FieldRequired',
    'AccountController_ResetPassword',
    'AccountController_Continue',
    'signupForm_RegisterEmailLabel',
    'signupForm_CreatePasswordTooltip',
    'resetPasswordForm_NewPasswordLabel',
    'resetPasswordForm_ConfirmPasswordLabel',
    'resetPasswordForm_InvalidEmail',
    'resetPasswordForm_InvalidPassword',
    'resetPasswordForm_InvalidConfirmPassword',
    'AccountSvc_PasswordResetError',
    'AccountSvc_ChangeLinkFail',
    'AccountSvc_ChangeSuccess',

    // new
    'AccountController_ChangePasswordTitle',
    'AccountController_OldPassword',
    'AccountController_NewPassword',
    'AccountController_ConfirmPassword',
  ]);

  public get degreedGlassesLogo(): string {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/branding/degreed-logo-glasses.svg',
      true
    );
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private builder: DfFormFieldBuilder,
    private accountService: AccountService,
    private webEnvironmentService: WebEnvironmentService,
    private router: Router,
    private authService: AuthService,
    private onboardingRequiredGuard: OnboardingRequiredGuard
  ) {
    super();
  }

  public ngAfterViewInit() {
    //  this.setHiddenInputs();
    this.fields = this.buildFormFields();
    this.cdr.detectChanges();
  }

  public submit() {
    this.isSaving = true;
    this.form.markAllAsTouched();
    this.validationSummary = null;

    if (this.form.invalid) {
      this.isSaving = false;
      return;
    }

    this.accountService
      .changePassword(
        this.form.value.currentPassword,
        this.form.value.newPassword,
        this.form.value.confirmPassword
      )
      .subscribe({
        next: () => {
          (
            this.onboardingRequiredGuard.canActivate(
              null,
              null
            ) as Observable<boolean>
          ).subscribe((skipOnboarding) => {
            if (skipOnboarding) {
              // TODO: use angular routing once page is properly routed by angular
              // this.router.navigateByUrl(
              //   `/profile/${this.authService.authUser.viewerProfile.profileUrl}/overview`
              // );
              window.location.href = `/profile/${this.authService.authUser.viewerProfile.profileUrl}/overview`;
            } else {
              // TODO: remove this else block. This will not be required once the page is
              // properly routed by angular. The onboarding required guard should automatically
              // reroute to onboarding
              window.location.href = `/getstarted/welcome`;
            }
          });
        },
        error: ({ error }: HttpErrorResponse) => {
          this.validationSummary = error.message;
          this.isSaving = false;
          this.cdr.detectChanges();
        },
      });
  }

  private buildFormFields(): DfFormFieldConfig[] {
    return [
      this.builder
        .currentPassword()
        .labeled(this.i18n.AccountController_OldPassword)
        .validatedBy(Validators.required)
        .autofocused()
        .build(),
      this.builder
        .passwordResetGroup(
          (newPassword) =>
            newPassword
              .labeled('AccountController_NewPassword')
              .withErrorMessages({
                required: this.i18n.resetPasswordForm_InvalidPassword,
              })
              .withHelp(this.i18n.signupForm_CreatePasswordTooltip)
              .build(),
          (confirmPassword) =>
            confirmPassword
              .labeled('AccountController_ConfirmPassword')
              .withErrorMessages({
                fieldMatch: this.i18n.resetPasswordForm_InvalidConfirmPassword,
                required: this.i18n.Core_FieldRequired,
              })
              .build()
        )
        .build(),
    ];
  }
}
